import { useQuery, keepPreviousData, useMutation, useQueryClient } from '@tanstack/react-query'
import { recoverRequestsApi, recoverySetsApi, type RecoverRequestDetail, type RecoverRequestStats, type RecoveryDependencyProgressRow, type RowScenarioInfo, type MobilisationTeam, type RecoverySetSummary, type RecoverySetDetail, type UnassignedRequestRow, type CreateRecoverySetPayload, type HierarchyForecastRow } from './api'
import { useAuthStore } from '@/app/store/auth'
import { useToast } from '@/hooks/use-toast'
import type { ListParams } from '@/lib/api/types'
import { myAssignmentKeys } from '@/features/my-assignments/hooks'

export const recoverRequestKeys = {
    all: ['recover-requests'] as const,
    lists: () => [...recoverRequestKeys.all, 'list'] as const,
    list: (params: ListParams) => [...recoverRequestKeys.lists(), params] as const,
}

export function useRecoverRequests(params?: ListParams & { requestID?: string; location?: string; team?: string; recoverySet?: string; scenario?: string }) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: recoverRequestKeys.list(params || {}),
        queryFn: () => recoverRequestsApi.list(params),
        enabled: isAuthenticated && !mfaRequired,
        placeholderData: keepPreviousData,
    })
}

export function useRequestIDOptions() {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: ['recover-requests', 'filter-options', 'request-ids'],
        queryFn: async () => {
            const response = await recoverRequestsApi.getRequestIDOptions()
            return response.data?.data || []
        },
        enabled: isAuthenticated && !mfaRequired,
    })
}

export function useLocationOptions() {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: ['recover-requests', 'filter-options', 'locations'],
        queryFn: async () => {
            const response = await recoverRequestsApi.getLocationOptions()
            return response.data?.data || []
        },
        enabled: isAuthenticated && !mfaRequired,
    })
}

export function useTeamOptions() {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: ['recover-requests', 'filter-options', 'teams'],
        queryFn: async () => {
            const response = await recoverRequestsApi.getTeamOptions()
            return response.data?.data || []
        },
        enabled: isAuthenticated && !mfaRequired,
    })
}

export function useRecoverySetOptions() {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: ['recover-requests', 'filter-options', 'recovery-sets'],
        queryFn: async () => {
            const response = await recoverRequestsApi.getRecoverySetOptions()
            return response.data?.data || []
        },
        enabled: isAuthenticated && !mfaRequired,
    })
}

export function useScenarioOptions() {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: ['recover-requests', 'filter-options', 'scenarios'],
        queryFn: async () => {
            const response = await recoverRequestsApi.getScenarioOptions()
            return response.data?.data || []
        },
        enabled: isAuthenticated && !mfaRequired,
    })
}

export function useRowScenarioInfo(requestID: string | null, scenario: number | null, enabled: boolean) {
    return useQuery({
        queryKey: ['recover-requests', 'row-info', requestID, scenario],
        queryFn: async () => {
            const response = await recoverRequestsApi.getRowInfo(requestID, scenario)
            return (response.data?.data) as RowScenarioInfo
        },
        enabled: Boolean(requestID != null && enabled),
    })
}

export function useRecoveryDependencyProgress(mainId: string | null, enabled: boolean) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: ['recover-requests', 'dependency-progress', mainId],
        queryFn: async () => {
            if (!mainId) return []
            const response = await recoverRequestsApi.getRecoveryDependencyProgress(mainId)
            return (response.data?.data ?? response.data) as RecoveryDependencyProgressRow[]
        },
        enabled: Boolean(isAuthenticated && !mfaRequired && mainId && enabled),
    })
}

export function useAssignEngineer() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: async ({ requestID, userID }: { requestID: number | null; userID: string | number }) => {
            console.log('🔍 DEBUG useAssignEngineer mutationFn:', {
                requestID,
                requestIDType: typeof requestID,
                userID,
                userIDType: typeof userID
            })
            const response = await recoverRequestsApi.assignEngineer(requestID, userID)
            console.log('🔍 DEBUG useAssignEngineer response:', response)
            return response.data
        },
        onSuccess: () => {
            // Invalidate recover requests to refresh the list
            queryClient.invalidateQueries({ queryKey: recoverRequestKeys.all })
            toast({
                title: 'Engineer assigned',
                description: 'The engineer has been successfully assigned to the recovery request.',
            })
        },
        onError: (error: any) => {
            toast({
                title: 'Failed to assign engineer',
                description: error?.response?.data?.message || error.message || 'An error occurred while assigning the engineer.',
                variant: 'destructive',
            })
        },
    })
}

export function useUnassignEngineer() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: async ({ requestID }: { requestID: number | null }) => {
            const response = await recoverRequestsApi.unassignEngineer(requestID)
            return response.data
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: recoverRequestKeys.all })
            queryClient.invalidateQueries({ queryKey: myAssignmentKeys.all })
            if (variables.requestID != null) {
                queryClient.removeQueries({ queryKey: ['recovery-runbook', variables.requestID] })
            }
            toast({
                title: 'Unassigned',
                description: 'You have been unassigned from the recovery request.',
            })
        },
        onError: (error: any) => {
            toast({
                title: 'Failed to unassign',
                description: error?.response?.data?.message || error.message || 'An error occurred while unassigning.',
                variant: 'destructive',
            })
        },
    })
}

export function useMarkComplete() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: async ({ requestID }: { requestID: number }) => {
            const response = await recoverRequestsApi.markComplete(requestID)
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: recoverRequestKeys.all })
            queryClient.invalidateQueries({ queryKey: myAssignmentKeys.all })
            toast({
                title: 'Request Completed',
                description: 'The recovery request has been marked as complete.',
            })
        },
        onError: (error: any) => {
            toast({
                title: 'Failed to mark complete',
                description: error?.response?.data?.message || error.message || 'An error occurred.',
                variant: 'destructive',
            })
        },
    })
}

export function useAssignableUsers(objectID?: string | null, responsibleTeamId?: number | null, locationId?: number | null) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    console.log('🔍 DEBUG useAssignableUsers hook:', {
        objectID,
        responsibleTeamId,
        locationId,
        responsibleTeamIdType: typeof responsibleTeamId,
        locationIdType: typeof locationId,
        isAuthenticated,
        mfaRequired
    })

    return useQuery({
        queryKey: ['recover-requests', 'assignable-users', objectID, responsibleTeamId, locationId],
        queryFn: async () => {
            console.log('🔍 DEBUG useAssignableUsers queryFn:', {
                objectID: objectID || undefined,
                responsibleTeamId: responsibleTeamId || undefined,
                locationId: locationId || undefined
            })
            const response = await recoverRequestsApi.getAssignableUsers(objectID || undefined, responsibleTeamId || undefined, locationId || undefined)
            console.log('🔍 DEBUG useAssignableUsers response:', {
                userCount: response.data?.data?.length || 0,
                users: response.data?.data
            })
            return response.data?.data || []
        },
        enabled: isAuthenticated && !mfaRequired,
    })
}

export function useRecoverRequestsByObjectID(objectID: string | null, requestID?: string | null) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery<RecoverRequestDetail[]>({
        queryKey: ['recover-requests', 'by-object', objectID, requestID],
        queryFn: async () => {
            if (!objectID) return []
            const response = await recoverRequestsApi.getByObjectID(objectID, requestID || undefined)
            return response.data?.data || []
        },
        enabled: isAuthenticated && !mfaRequired && !!objectID,
    })
}

export function useRecoverRequestStats() {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery<RecoverRequestStats>({
        queryKey: ['recover-requests', 'stats'],
        queryFn: async () => {
            const response = await recoverRequestsApi.getStats()
            return response.data?.data || { uniqueRequestCount: 0, objectCount: 0 }
        },
        enabled: isAuthenticated && !mfaRequired,
    })
}

export function useStartRecovery() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: async ({ requestID, objectID, scenario }: { requestID: number; objectID: string; scenario: number }) => {
            const response = await recoverRequestsApi.startRecovery(requestID, objectID, scenario)
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: recoverRequestKeys.all })
            queryClient.invalidateQueries({ queryKey: myAssignmentKeys.all })
            toast({
                title: 'Recovery started',
                description: 'The recovery runbook has been created successfully.',
            })
        },
        onError: (error: any) => {
            toast({
                title: 'Failed to start recovery',
                description: error?.response?.data?.message || error.message || 'An error occurred while starting recovery.',
                variant: 'destructive',
            })
        },
    })
}

const MOBILISATION_STEP_LABELS = [
    'Activate Users',
    'Communication',
    'Mobilisation Tasks',
    'Technical Checklist',
    'Impact Assessment',
]

export function useMobilisationStatus(requestID: number | null) {
    return useQuery({
        queryKey: ['recovery-runbook', requestID, 'mobilisation-status'],
        queryFn: async () => {
            if (requestID == null) return { completedSteps: 0, totalSteps: 5 as const }
            try {
                const res = await recoverRequestsApi.getMobilisationStatus(requestID)
                const data = res.data?.data
                return { completedSteps: data?.completedSteps ?? 0, totalSteps: 5 as const }
            } catch (err: unknown) {
                const status = (err as { response?: { status?: number } })?.response?.status
                if (status === 404) return { completedSteps: 0, totalSteps: 5 as const }
                throw err
            }
        },
        enabled: requestID != null && requestID > 0,
    })
}

export function useMobilisationTeams(requestID: number | null) {
    return useQuery({
        queryKey: ['recovery-runbook', requestID, 'mobilisation-teams'],
        queryFn: async (): Promise<MobilisationTeam[]> => {
            if (requestID == null) return []
            try {
                const res = await recoverRequestsApi.getMobilisationTeams(requestID)
                return res.data?.data ?? []
            } catch (err: unknown) {
                const status = (err as { response?: { status?: number } })?.response?.status
                if (status === 404) return []
                throw err
            }
        },
        enabled: requestID != null && requestID > 0,
    })
}

export function useMobiliseUsers(requestID: number | null) {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    return useMutation({
        mutationFn: () => recoverRequestsApi.mobiliseUsers(requestID!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['recovery-runbook', requestID, 'mobilisation-status'] })
            queryClient.invalidateQueries({ queryKey: ['recovery-runbook', requestID, 'mobilisation-teams'] })
            toast({ title: 'Users activated', description: 'Recovery user accounts have been activated.' })
        },
        onError: (err: any) => {
            toast({ title: 'Activation failed', description: err?.response?.data?.message || err.message, variant: 'destructive' })
        },
    })
}

export function useSendTeamNotification(requestID: number | null) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({ teamId, channel }: { teamId: number; channel: 'sms' | 'email' }) =>
            recoverRequestsApi.sendTeamNotification(requestID!, teamId, channel),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['recovery-runbook', requestID, 'mobilisation-status'] })
        },
    })
}

export function useSendAllNotifications(requestID: number | null) {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    return useMutation({
        mutationFn: () => recoverRequestsApi.sendAllNotifications(requestID!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['recovery-runbook', requestID, 'mobilisation-status'] })
            toast({ title: 'Notifications sent', description: 'All teams have been notified.' })
        },
        onError: (err: any) => {
            toast({ title: 'Send failed', description: err?.response?.data?.message || err.message, variant: 'destructive' })
        },
    })
}

export { MOBILISATION_STEP_LABELS }

// Recovery Sets (Recovery Manager)
export const recoverySetKeys = {
    all: ['recovery-sets'] as const,
    list: () => [...recoverySetKeys.all, 'list'] as const,
    dashboard: () => [...recoverySetKeys.all, 'dashboard'] as const,
    detail: (setId: number) => [...recoverySetKeys.all, 'detail', setId] as const,
    requestIdsInSet: (setId: number) => [...recoverySetKeys.all, 'detail', setId, 'request-ids'] as const,
    unassigned: (params?: { page?: number; limit?: number }) => [...recoverySetKeys.all, 'unassigned', params] as const,
    hierarchyForecast: (requestID: string) => [...recoverySetKeys.all, 'unassigned', requestID, 'hierarchy-forecast'] as const,
    locationTracking: (setId: number, requestId?: string) => [...recoverySetKeys.all, 'detail', setId, 'location-tracking', requestId ?? null] as const,
}

/**
 * Location Tracking feed (read-only). Polls the two stored procedures via the
 * backend. `refreshMs` of 0 (or null) disables polling; the caller's interval
 * control sets it. Returns the normalized { locations, objects } shape.
 */
export function useLocationTracking(
    setId: number | null,
    opts?: { requestId?: string; refreshMs?: number },
) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)
    const refreshMs = opts?.refreshMs ?? 0
    return useQuery({
        queryKey: recoverySetKeys.locationTracking(setId ?? -1, opts?.requestId),
        queryFn: async () => {
            const res = await recoverySetsApi.getLocationTracking(setId as number, opts?.requestId)
            return res.data?.data ?? { locations: [], objects: [], scope: { option: '', requestId: null, recoverySet: 0 } }
        },
        enabled: isAuthenticated && !mfaRequired && setId != null && setId > 0,
        refetchInterval: refreshMs > 0 ? refreshMs : false,
        refetchOnWindowFocus: false,
    })
}

export function useRecoverySets() {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)
    return useQuery({
        queryKey: recoverySetKeys.list(),
        queryFn: async (): Promise<RecoverySetSummary[]> => {
            const res = await recoverySetsApi.list()
            return res.data?.data ?? []
        },
        enabled: isAuthenticated && !mfaRequired,
    })
}

export function useRecoverySetDashboard() {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)
    return useQuery({
        queryKey: recoverySetKeys.dashboard(),
        queryFn: async () => {
            const res = await recoverySetsApi.getDashboard()
            const data = res.data?.data
            return {
                summary: data?.summary ?? { percentActivated: 0, noMessagesSent: 0, noRed: 0 },
                statisticsBySetId: data?.statisticsBySetId ?? {},
            }
        },
        enabled: isAuthenticated && !mfaRequired,
    })
}

export function useRecoverySet(setId: number | null) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)
    return useQuery({
        queryKey: recoverySetKeys.detail(setId!),
        queryFn: async (): Promise<RecoverySetDetail | null> => {
            if (setId == null) return null
            const res = await recoverySetsApi.get(setId)
            return res.data?.data ?? null
        },
        enabled: isAuthenticated && !mfaRequired && setId != null && setId > 0,
    })
}

export function useCreateRecoverySet() {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    return useMutation({
        mutationFn: (payload: CreateRecoverySetPayload) => recoverySetsApi.create(payload),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: recoverySetKeys.list() })
            const data = res.data?.data
            toast({
                title: 'Recovery set created',
                description: data ? `"${data.name}" created successfully.` : 'Recovery set created.',
            })
        },
        onError: (err: { response?: { status?: number; data?: { message?: string } }; message?: string }) => {
            const msg = err?.response?.data?.message || err?.message || 'Failed to create recovery set.'
            const is409 = err?.response?.status === 409
            toast({
                title: is409 ? 'Name already in use' : 'Failed to create recovery set',
                description: msg,
                variant: 'destructive',
            })
        },
    })
}

export function useUnassignedRequestIDs(params?: { page?: number; limit?: number }) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)
    return useQuery({
        queryKey: recoverySetKeys.unassigned(params),
        queryFn: async () => {
            const res = await recoverySetsApi.getUnassignedRequests(params)
            const payload = res.data as { data?: UnassignedRequestRow[]; total?: number; page?: number; perPage?: number }
            return { list: payload?.data ?? [], total: payload?.total ?? 0, page: payload?.page ?? 1, perPage: payload?.perPage ?? 50 }
        },
        enabled: isAuthenticated && !mfaRequired,
    })
}

export function useHierarchyForecast(requestID: string | null) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)
    return useQuery({
        queryKey: recoverySetKeys.hierarchyForecast(requestID ?? ''),
        queryFn: async (): Promise<HierarchyForecastRow[]> => {
            if (!requestID) return []
            const res = await recoverySetsApi.getHierarchyForecast(requestID)
            return res.data?.data ?? []
        },
        enabled: isAuthenticated && !mfaRequired && !!requestID,
    })
}

export function useRequestIdsInSet(setId: number | null) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)
    return useQuery({
        queryKey: recoverySetKeys.requestIdsInSet(setId!),
        queryFn: async (): Promise<UnassignedRequestRow[]> => {
            if (setId == null) return []
            const res = await recoverySetsApi.getRequestIdsInSet(setId)
            return res.data?.data ?? []
        },
        enabled: isAuthenticated && !mfaRequired && setId != null && setId > 0,
    })
}

export function useAddRequestIdToSet(setId: number | null) {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    return useMutation({
        mutationFn: (payload: { requestID: string; scenario?: number | null }) =>
            recoverySetsApi.addRequestIdToSet(setId!, payload.requestID, payload.scenario),
        onSuccess: (_, payload) => {
            const requestID = typeof payload === 'string' ? payload : payload.requestID
            queryClient.invalidateQueries({ queryKey: recoverySetKeys.requestIdsInSet(setId!) })
            queryClient.invalidateQueries({ queryKey: [...recoverySetKeys.all, 'unassigned'] })
            queryClient.invalidateQueries({ queryKey: recoverySetKeys.detail(setId!) })
            queryClient.invalidateQueries({ queryKey: recoverySetKeys.list() })
            toast({ title: 'Added to set', description: `Request ${requestID} added to recovery set.` })
        },
        onError: (err: unknown) => {
            const msg = err instanceof Error ? err.message : 'Failed to add request to set'
            toast({ variant: 'destructive', title: 'Error', description: msg })
        },
    })
}

export type ConflictOptionForecastStatus = {
    inProgress: boolean
    totalTasks?: number | null
    startedTasks?: number | null
    completedTasks?: number | null
    issueTasks?: number | null
    percentComplete?: number | null
    engineerName?: string | null
    forecastFinishTime?: string | null
}

export type AddRequestConflictOption = {
    requestId: string | null
    objectversionsId: number | null
    vaultDatetime: string | null
    analytics: number | null
    forecastStatus?: ConflictOptionForecastStatus
}
export type AddRequestConflict = {
    objectId: string
    objectName: string
    options: AddRequestConflictOption[]
}

/** Conflict row from RecoverySetObjectIDConflicts view (manifest = existing in set, external = request being added) */
export type ViewConflictSide = {
    objectversionsId?: number | null
    assignmentStatus?: string | null
    engineerName?: string | null
    vaultdatetime?: string | null
    totalTasks?: number | null
    completedTasks?: number | null
    startedTasks?: number | null
    issueTasks?: number | null
    percentComplete?: number | null
}
export type ViewConflictRow = {
    objectId: string
    objectName: string
    manifestRequestId: string | null
    externalRequestId: string
    useView?: boolean
    manifest: ViewConflictSide
    external: ViewConflictSide
}

export type CheckAddRequestResponse =
    | { status: 'clean' }
    | { status: 'conflicts'; conflicts: ViewConflictRow[]; useView: true }

export function useAddRequestToSetWithResolutions(setId: number | null) {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    return useMutation({
        mutationFn: (payload: { requestId: string; userId?: number; resolutions: Array<{ objectId: string; priorityRequestId: string }> }) =>
            recoverySetsApi.addRequestToSetWithResolutions(setId!, payload),
        onSuccess: (_, payload) => {
            queryClient.invalidateQueries({ queryKey: recoverySetKeys.requestIdsInSet(setId!) })
            queryClient.invalidateQueries({ queryKey: [...recoverySetKeys.all, 'unassigned'] })
            queryClient.invalidateQueries({ queryKey: recoverySetKeys.detail(setId!) })
            queryClient.invalidateQueries({ queryKey: recoverySetKeys.list() })
            toast({ title: 'Added to set', description: `Request ${payload.requestId} added to recovery set.` })
        },
        onError: (err: unknown) => {
            const msg = err instanceof Error ? err.message : 'Failed to add request to set'
            toast({ variant: 'destructive', title: 'Error', description: msg })
        },
    })
}

export function useRemoveRequestIdFromSet(setId: number | null) {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    return useMutation({
        mutationFn: (requestID: string) => recoverySetsApi.removeRequestIdFromSet(setId!, requestID),
        onSuccess: (_, requestID) => {
            queryClient.invalidateQueries({ queryKey: recoverySetKeys.requestIdsInSet(setId!) })
            queryClient.invalidateQueries({ queryKey: [...recoverySetKeys.all, 'unassigned'] })
            queryClient.invalidateQueries({ queryKey: recoverySetKeys.detail(setId!) })
            queryClient.invalidateQueries({ queryKey: recoverySetKeys.list() })
            toast({ title: 'Removed from set', description: `Request ${requestID} removed from recovery set.` })
        },
        onError: (err: unknown) => {
            const msg = err instanceof Error ? err.message : 'Failed to remove request from set'
            toast({ variant: 'destructive', title: 'Error', description: msg })
        },
    })
}

// Set-based mobilisation (Recovery Set detail)
export function useMobilisationStatusBySet(setId: number | null) {
    return useQuery({
        queryKey: ['recovery-sets', setId, 'mobilisation-status'],
        queryFn: async () => {
            if (setId == null) return { completedSteps: 0, totalSteps: 5 as const }
            try {
                const res = await recoverySetsApi.getMobilisationStatus(setId)
                const data = res.data?.data
                return { completedSteps: data?.completedSteps ?? 0, totalSteps: (data?.totalSteps ?? 5) as 5 }
            } catch (err: unknown) {
                const status = (err as { response?: { status?: number } })?.response?.status
                if (status === 404) return { completedSteps: 0, totalSteps: 5 as const }
                throw err
            }
        },
        enabled: setId != null && setId > 0,
    })
}

export function useMobilisationTeamsBySet(setId: number | null) {
    return useQuery({
        queryKey: ['recovery-sets', setId, 'mobilisation-teams'],
        queryFn: async (): Promise<MobilisationTeam[]> => {
            if (setId == null) return []
            try {
                const res = await recoverySetsApi.getMobilisationTeams(setId)
                return res.data?.data ?? []
            } catch (err: unknown) {
                const status = (err as { response?: { status?: number } })?.response?.status
                if (status === 404) return []
                throw err
            }
        },
        enabled: setId != null && setId > 0,
    })
}

export function useMobiliseUsersBySet(setId: number | null) {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    return useMutation({
        mutationFn: () => recoverySetsApi.mobiliseUsers(setId!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['recovery-sets', setId, 'mobilisation-status'] })
            queryClient.invalidateQueries({ queryKey: ['recovery-sets', setId, 'mobilisation-teams'] })
            queryClient.invalidateQueries({ queryKey: recoverySetKeys.list() })
            toast({ title: 'Users activated', description: 'Recovery user accounts have been activated.' })
        },
        onError: (err: any) => {
            toast({ title: 'Activation failed', description: err?.response?.data?.message || err.message, variant: 'destructive' })
        },
    })
}

export function useDeactivateUsersBySet(setId: number | null) {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    return useMutation({
        mutationFn: () => recoverySetsApi.deactivateUsers(setId!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['recovery-sets', setId, 'mobilisation-status'] })
            queryClient.invalidateQueries({ queryKey: ['recovery-sets', setId, 'mobilisation-teams'] })
            toast({ title: 'Users deactivated', description: 'Recovery user accounts have been deactivated.' })
        },
        onError: (err: any) => {
            toast({ title: 'Deactivation failed', description: err?.response?.data?.message || err.message, variant: 'destructive' })
        },
    })
}

export function useActivateTeamUsersBySet(setId: number | null) {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    return useMutation({
        mutationFn: ({ teamId }: { teamId: number }) => recoverySetsApi.activateTeamUsers(setId!, teamId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['recovery-sets', setId, 'mobilisation-status'] })
            queryClient.invalidateQueries({ queryKey: ['recovery-sets', setId, 'mobilisation-teams'] })
            toast({ title: 'Team activated', description: 'Team standby users have been activated.' })
        },
        onError: (err: any) => {
            toast({ title: 'Activation failed', description: err?.response?.data?.message || err.message, variant: 'destructive' })
        },
    })
}

export function useDeactivateTeamUsersBySet(setId: number | null) {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    return useMutation({
        mutationFn: ({ teamId }: { teamId: number }) => recoverySetsApi.deactivateTeamUsers(setId!, teamId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['recovery-sets', setId, 'mobilisation-status'] })
            queryClient.invalidateQueries({ queryKey: ['recovery-sets', setId, 'mobilisation-teams'] })
            toast({ title: 'Team deactivated', description: 'Team standby users have been deactivated.' })
        },
        onError: (err: any) => {
            toast({ title: 'Deactivation failed', description: err?.response?.data?.message || err.message, variant: 'destructive' })
        },
    })
}

export type SendResult = { sent: number; failed?: number; errorMessage?: string }

export function useSendTeamNotificationBySet(
    setId: number | null,
    options?: {
        onSendComplete?: (teamId: number, channel: 'sms' | 'email', result: SendResult) => void
        onProgress?: (teamId: number, channel: 'sms' | 'email', sent: number, total: number) => void
        onSendError?: (teamId: number, channel: 'sms' | 'email') => void
    }
) {
    const { toast } = useToast()
    return useMutation({
        mutationFn: async ({ teamId, channel }: { teamId: number; channel: 'sms' | 'email' }) => {
            if (options?.onProgress) {
                const token = useAuthStore.getState().accessToken
                return recoverySetsApi.sendTeamNotificationStream(setId!, teamId, channel, {
                    token: token ?? undefined,
                    onProgress: (sent, total) => options?.onProgress?.(teamId, channel, sent, total),
                })
            }
            const res = await recoverySetsApi.sendTeamNotification(setId!, teamId, channel)
            const payload = res?.data?.data ?? res?.data ?? {}
            return {
                sent: typeof payload.sent === 'number' ? payload.sent : 0,
                failed: typeof payload.failed === 'number' ? payload.failed : 0,
                errorMessage: typeof payload.errorMessage === 'string' ? payload.errorMessage : undefined,
            }
        },
        onSuccess: (data: { sent: number; failed?: number; errorMessage?: string }, variables) => {
            const sent = typeof data?.sent === 'number' ? data.sent : 0
            const failed = typeof data?.failed === 'number' ? data.failed : 0
            const errorMessage = typeof data?.errorMessage === 'string' ? data.errorMessage : undefined
            const channelLabel = variables?.channel === 'sms' ? 'SMS' : 'Email'
            if (failed > 0 && errorMessage) {
                toast({ title: `${channelLabel} failed`, description: errorMessage, variant: 'destructive' })
            } else if (sent > 0) {
                toast({ title: `${channelLabel} sent`, description: `Sent to ${sent} recipient(s).` })
            } else {
                toast({ title: `${channelLabel} sent`, description: 'Notification request completed.' })
            }
            options?.onSendComplete?.(variables.teamId, variables.channel, { sent, failed: failed || undefined, errorMessage })
        },
        onError: (err: any, variables) => {
            options?.onSendError?.(variables?.teamId, variables?.channel)
            const msg = err?.response?.data?.message ?? err?.message ?? 'Send failed'
            toast({ title: 'Send failed', description: msg, variant: 'destructive' })
        },
    })
}

export type NotifyAllProgressEvent = import('../api').NotifyAllProgressEvent

export function useSendAllNotificationsBySet(
    setId: number | null,
    options?: { onProgress?: (event: NotifyAllProgressEvent) => void }
) {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    return useMutation({
        mutationFn: async () => {
            const token = useAuthStore.getState().accessToken
            return recoverySetsApi.sendAllNotificationsStream(setId!, {
                onProgress: options?.onProgress,
                token: token ?? undefined,
            })
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['recovery-sets', setId, 'mobilisation-status'] })
            const sent = data?.sent ?? 0
            const failed = data?.failed ?? 0
            toast({
                title: 'Notifications sent',
                description: failed > 0 ? `${sent} sent, ${failed} failed.` : 'All teams have been notified.',
            })
        },
        onError: (err: any) => {
            toast({ title: 'Send failed', description: err?.response?.data?.message || err.message, variant: 'destructive' })
        },
    })
}

export function useTaskActionsBySet(setId: number | null, recoveryMobilisationId: number | null) {
    return useQuery({
        queryKey: ['recovery-sets', setId, 'mobilisation-task-actions', recoveryMobilisationId],
        queryFn: async () => {
            if (setId == null || recoveryMobilisationId == null) return []
            const res = await recoverySetsApi.getTaskActions(setId, recoveryMobilisationId)
            return res.data?.data ?? []
        },
        enabled: setId != null && setId > 0 && recoveryMobilisationId != null && recoveryMobilisationId > 0,
    })
}

export function useSaveTaskActionBySet(setId: number | null) {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    return useMutation({
        mutationFn: ({
            recoveryMobilisationId,
            action,
            comments,
        }: {
            recoveryMobilisationId: number
            action: string
            comments?: string
        }) => recoverySetsApi.saveTaskAction(setId!, recoveryMobilisationId, { action, comments }),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['recovery-sets', setId, 'mobilisation-teams'] })
            queryClient.invalidateQueries({ queryKey: ['recovery-sets', setId, 'mobilisation-task-actions', variables.recoveryMobilisationId] })
            toast({ title: 'Saved', description: 'Task status and comments saved.' })
        },
        onError: (err: any) => {
            toast({ title: 'Save failed', description: err?.response?.data?.message || err.message, variant: 'destructive' })
        },
    })
}

export function useTechnicalTaskActionsBySet(setId: number | null, recoveryTaskId: number | null) {
    return useQuery({
        queryKey: ['recovery-sets', setId, 'technical-task-actions', recoveryTaskId],
        queryFn: async () => {
            if (setId == null || recoveryTaskId == null) return []
            const res = await recoverySetsApi.getTechnicalTaskActions(setId, recoveryTaskId)
            return res.data?.data ?? []
        },
        enabled: setId != null && setId > 0 && recoveryTaskId != null && recoveryTaskId > 0,
    })
}

export function useSaveTechnicalTaskActionBySet(setId: number | null) {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    return useMutation({
        mutationFn: ({
            recoveryTaskId,
            action,
            comments,
        }: {
            recoveryTaskId: number
            action: string
            comments?: string
        }) => recoverySetsApi.saveTechnicalTaskAction(setId!, recoveryTaskId, { action, comments }),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['recovery-sets', setId, 'mobilisation-teams'] })
            queryClient.invalidateQueries({ queryKey: ['recovery-sets', setId, 'technical-task-actions', variables.recoveryTaskId] })
            toast({ title: 'Saved', description: 'Check status and comments saved.' })
        },
        onError: (err: any) => {
            toast({ title: 'Save failed', description: err?.response?.data?.message || err.message, variant: 'destructive' })
        },
    })
}

export function useImpactRecoveryObjects(setId: number | null, params?: { search?: string; location?: number | null; team?: number | null }) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)
    return useQuery({
        queryKey: ['recovery-sets', setId, 'impact-recovery-objects', params?.search ?? '', params?.location ?? '', params?.team ?? ''],
        queryFn: async () => {
            const res = await recoverySetsApi.getImpactRecoveryObjects(setId!, { search: params?.search, location: params?.location ?? undefined, team: params?.team ?? undefined })
            return res.data?.data ?? []
        },
        enabled: isAuthenticated && !mfaRequired && setId != null && setId > 0,
    })
}

export function useImpactLocationOptions(setId: number | null) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)
    return useQuery({
        queryKey: ['recovery-sets', setId, 'impact-filter-options', 'locations'],
        queryFn: async () => {
            const res = await recoverySetsApi.getImpactLocationOptions(setId!)
            return res.data?.data ?? []
        },
        enabled: isAuthenticated && !mfaRequired && setId != null && setId > 0,
    })
}

export function useImpactTeamOptions(setId: number | null) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)
    return useQuery({
        queryKey: ['recovery-sets', setId, 'impact-filter-options', 'teams'],
        queryFn: async () => {
            const res = await recoverySetsApi.getImpactTeamOptions(setId!)
            return res.data?.data ?? []
        },
        enabled: isAuthenticated && !mfaRequired && setId != null && setId > 0,
    })
}

export function useImpactRecoveryObjectStatus(setId: number | null, params?: { search?: string; filter?: 'all' | 'amber' | 'services' | 'amber-services'; scenario?: number | null }) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)
    return useQuery({
        queryKey: ['recovery-sets', setId, 'impact-recovery-object-status', params?.search ?? '', params?.filter ?? 'all', params?.scenario ?? ''],
        queryFn: async () => {
            const res = await recoverySetsApi.getImpactRecoveryObjectStatus(setId!, { search: params?.search, filter: params?.filter, scenario: params?.scenario ?? undefined })
            const payload = res.data?.data
            if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
                return {
                    data: payload.data,
                    scenariosAffected: typeof payload.scenariosAffected === 'number' ? payload.scenariosAffected : 0,
                    affectedScenarioIds: Array.isArray(payload.affectedScenarioIds) ? payload.affectedScenarioIds : [],
                }
            }
            return { data: [], scenariosAffected: 0, affectedScenarioIds: [] }
        },
        enabled: isAuthenticated && !mfaRequired && setId != null && setId > 0,
    })
}

export function useUpdateImpactRecoveryObjectStatus(setId: number | null) {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    return useMutation({
        mutationFn: ({ id, objectStatus }: { id: number; objectStatus: 2 | 4 }) =>
            recoverySetsApi.updateImpactRecoveryObjectStatus(setId!, id, objectStatus),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['recovery-sets', setId, 'impact-recovery-objects'] })
            queryClient.invalidateQueries({ queryKey: ['recovery-sets', setId, 'impact-recovery-object-status'] })
            toast({ title: 'Saved', description: 'Impact status updated.' })
        },
        onError: (err: any) => {
            toast({ title: 'Update failed', description: err?.response?.data?.message || err.message, variant: 'destructive' })
        },
    })
}

export function useUpdateImpactRecoveryObject(setId: number | null) {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    return useMutation({
        mutationFn: ({ id, ...payload }: { id: number; objectStatus?: 2 | 4; issueDescription?: string }) =>
            recoverySetsApi.updateImpactRecoveryObject(setId!, id, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['recovery-sets', setId, 'impact-recovery-objects'] })
            toast({ title: 'Saved', description: 'Issue description updated.' })
        },
        onError: (err: any) => {
            toast({ title: 'Update failed', description: err?.response?.data?.message || err.message, variant: 'destructive' })
        },
    })
}
